import {Component} from '@angular/core';
import {LanguageService} from '../../../shared/services/language.service';

@Component({
    selector: 'course-exercise-access-support-article',
    template: `
        <ng-container i18n="@@support.articles.exerciseAccess">
            <p>When creating an exercise, you can choose how your students get access to the exercise.</p>
            <p>
                <img src="assets/images/support/{{ languageService.getActiveLanguageCode() }}/addExerciseAccess.png" />
            </p>

            <h2>My own students</h2>
            <p>When you are assigned to a class as an instructor, the students of that class automatically get access to this exercise.</p>
            <p>This is the <strong>default setting</strong>.</p>

            <h2>Every student in my organization</h2>
            <p>Every student in your organization automatically gets access to this exercise, even if you're not their instructor.</p>

            <h2>Grant manual access to classes</h2>
            <p>This allows you to tick each class individually when you want to grant access to the exercise.</p>
            <p>For example, you can make a test and give access to each class just moments before they are supposed to take the test. This gives the students no time to cheat.</p>

            <p>There are two ways to give classes access:</p>

            <h3>Give access by exercise</h3>
            <p>With this option you can select which classes you want to give access to for a certain exercise.</p>
            <ol>
                <li>
                    <span>Go to <a routerLink="/organization/exercises">My exercises</a>.</span>
                </li>
                <li>
                    <span>Find the exercise in the list or use the search field to find the exercise.</span>
                </li>
                <li>
                    <span>
                        Click on the three dots on the right side of the exercise.
                        <br />
                        <img src="assets/images/support/{{ languageService.getActiveLanguageCode() }}/exerciseAccessBtn.png" />
                    </span>
                </li>
                <li>
                    <span>
                        Click on <strong>Assign to classes</strong> in the menu that opens.
                        <br />
                        If this button does not appear, the access setting of the exercise is not set to <em>Give manual access to classes</em>.
                    </span>
                </li>
                <li>
                    <div>
                        <p>Tick the desired classes.</p>
                        <p>
                            <img src="assets/images/support/{{ languageService.getActiveLanguageCode() }}/assignExerciseToClasses.png" />
                        </p>
                    </div>
                </li>
                <li>
                    <span>Click <strong>Save</strong>.</span>
                </li>
                <li>
                    <span>Students of the selected classes get immediate access to the exercise.</span>
                </li>
            </ol>

            <h3>Give access by class</h3>
            <p>With this option you can select which exercises you want to assign to a certain class.</p>
            <ol>
                <li>
                    <span>Go to the <a routerLink="/organization/classes">Classes Overview</a>.</span>
                </li>
                <li>
                    <span>Find the class in the list or use the search field to find the class.</span>
                </li>
                <li>
                    <span>Click on the name of the class.</span>
                </li>
                <li>
                    <span>Open the <strong>Extra exercises</strong> tab.</span>
                </li>
                <li>
                    <div>
                        <p>In the bottom box you can see which exercises are always available for this class. These are exercises with the access setting <em>My own students</em> or <em>Any student in my organization</em>. These exercises can therefore not be checked or unchecked manually.</p>
                        <p>In the top box you can see which exercises are available for manual access.</p>
                        <figure>
                            <img src="assets/images/support/{{ languageService.getActiveLanguageCode() }}/classExerciseAccess.png" />
                            <figcaption>Tip: click on the eye icon to preview the exercise.</figcaption>
                        </figure>
                    </div>
                </li>
                <li>
                    <span>Check the exercises you want to assign to this class.</span>
                </li>
                <li>
                    <span>Click <strong>Save</strong>.</span>
                </li>
                <li>
                    <span>The students of this class now have access to the selected exercises.</span>
                </li>
            </ol>
        </ng-container>
    `
})
export class ExerciseAccessSupportArticleComponent {
    constructor(
        public languageService: LanguageService
    ) {}
}
